import {
  useEffectWithController,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import { MAX_PAGE_SIZE_DEFAULT } from '~/constants/srp';
import { useIsLoggedIn } from '~/context/common/UserContext';
import {
  useIsServerDriven,
  useServerNavToken
} from '~/context/srp/SrpNavModeContext';
import SuggestedFiltersContext from '~/context/srp/SuggestedFiltersContext';
import useGenerateAnalyticsData from '~/hooks/srp/useGenerateAnalyticsData';
import useGenerateInventoryRequest from '~/hooks/srp/useGenerateInventoryRequest';
import fetchSuggestedFilters from '~/services/common/fetchSuggestedFilters';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetSuggestedFilters(): void {
  const {
    setSuggestedFiltersRequestId,
    suggestedFilters: { setData, setError, setLoaded, setLoading }
  } = useContext(SuggestedFiltersContext);

  const isLoggedIn = useIsLoggedIn();
  const isServerDriven = useIsServerDriven();
  const serverNavToken = useServerNavToken();
  const generateAnalyticsData = useGenerateAnalyticsData();
  const { request, conditionalRequestedFeatures } = useGenerateInventoryRequest(
    MAX_PAGE_SIZE_DEFAULT,
    {
      includePageNumber: true,
      limitRequestedFeatures: !isLoggedIn
    }
  );

  // Server-driven: revert chips to loading on a real nav (nav-token bump); the
  // hydrator re-marks loaded. useSubsequentEffect skips mount (no initial flash).
  useSubsequentEffect(() => {
    if (isServerDriven) setLoading();
  }, [isServerDriven, serverNavToken, setLoading]);

  useEffectWithController(
    controller => {
      // cars-rsc streams suggested filters via an RSC island; never client-fetch.
      if (isServerDriven || request.dealershipId) return;

      setLoading();

      fetchSuggestedFilters(
        {
          ...request,
          analyticsData: generateAnalyticsData(),
          requestedFeatures: request.requestedFeatures.concat(
            ...conditionalRequestedFeatures
          ),
          numberOfSuggestedFilters: 10
        },
        controller
      )
        .then(({ data }) => {
          setData(data.suggestedFilters);
          setSuggestedFiltersRequestId(data.suggestedFiltersRequestId);
        })
        .catch(
          generateErrorLogger(
            'Suggested filters',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      conditionalRequestedFeatures,
      generateAnalyticsData,
      isServerDriven,
      request,
      setData,
      setError,
      setLoaded,
      setLoading,
      setSuggestedFiltersRequestId
    ]
  );
}
